import React, { useEffect, useRef, useState } from 'react';
import { Card, Col, Form, Row, Button } from 'react-bootstrap';
import PropTypes from 'prop-types';
import axios from "axios";
import { toast } from 'react-toastify';
import { Link } from 'react-router-dom';
import Spinner from 'react-bootstrap/Spinner';
import { createIframeReportBlobUrl } from 'helpers/iframeReportPreview';
import AdvanceTable from 'components/common/advance-table/AdvanceTable';
import AdvanceTableSearchBox from 'components/common/advance-table/AdvanceTableSearchBox';
import AdvanceTableFooter from 'components/common/advance-table/AdvanceTableFooter';
import useAdvanceTable from 'hooks/useAdvanceTable';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import paths from 'routes/paths';

const LIST_URL = `${process.env.REACT_APP_SERVER_URL}/reports/medicalrecords-list`;
const REPORT_URL = `${process.env.REACT_APP_SERVER_URL}/reports/medicalrecords-report`;

// API response key isn't fixed yet — accept the common shapes.
const extractRows = (data) => {
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.data)) return data.data;
    if (Array.isArray(data?.medicalRecords)) return data.medicalRecords;
    if (Array.isArray(data?.medical_records)) return data.medical_records;
    return [];
};

const MedicalReport = (props) => {
    const [loader, setLoader] = useState(false);
    const [spinner, showSpinner] = useState(false);
    const [iframeSrc, setIframeSrc] = useState(null);
    const [tableData, setTableData] = useState([]);
    const [wholeData, setWholeData] = useState([]);
    const tableRef = useRef(null);
    const [formData, setFormData] = useState({
        isShowAllFacility: false,
    });

    const facilityList = JSON.parse(localStorage.getItem("facilitiesData")) || [];
    const facilityObj = facilityList.reduce((obj, item) => {
        obj[item.value] = item.key;
        return obj;
    }, {});

    const columns = [
        {
            accessorKey: 'facility_name',
            header: 'Facility Name',
            meta: {
                headerProps: { className: 'text-900 text-start' },
                cellProps: { className: 'text-start' }
            }
        },
        {
            accessorKey: 'patient_name',
            header: 'Patient Name',
            meta: {
                headerProps: { className: 'text-900 text-start' },
                cellProps: { className: 'text-start' }
            },
            cell: ({ row: { original } }) => (
                original.patient_id ? (
                    <Link to={{ pathname: `${paths.patientDetails}/${original.patient_id}` }}>
                        {original.patient_name}
                    </Link>
                ) : original.patient_name
            )
        },
        {
            accessorKey: 'medical_record_category',
            header: 'Medical Record Category',
            meta: {
                headerProps: { className: 'text-900 text-start' },
                cellProps: { className: 'text-start' }
            },
            // Opens the medical record's edit page; it loads the record by the id in the URL.
            cell: ({ row: { original } }) => (
                original.medical_record_id ? (
                    <Link to={{ pathname: `${paths.editMedicalRecord}/${original.medical_record_id}` }}>
                        {original.medical_record_category}
                    </Link>
                ) : original.medical_record_category
            )
        }
    ];

    const mapRows = (rows) => rows.map(item => ({
        medical_record_id: item.medical_record_id,
        patient_id: item.patient_id,
        facility_name: item.facility_name || facilityObj[item.facility_id] || '',
        patient_name: [item.last_name, item.first_name].filter(Boolean).join(', '),
        medical_record_category: item.medical_record_category_desc || ''
    }));

    const getHeaders = () => ({
        headers: { 'Authorization': `Bearer ${localStorage.getItem("accessToken")}` }
    });

    useEffect(() => {
        fetchAllData();
    }, []);

    useEffect(() => {
        if (props.selectedFacility) fetchData();
    }, [props.selectedFacility]);

    const fetchAllData = async () => {
        setLoader(true);
        try {
            const { data } = await axios.post(LIST_URL, { "facility_id": '-999', }, getHeaders());
            setWholeData(mapRows(extractRows(data)));
        } catch (error) {
            console.error("Error fetching medical records:", error);
        } finally {
            setLoader(false);
        }
        setFormData(prev => ({ ...prev, isShowAllFacility: true })); // default checked
        props.setFlag(true);
    };

    const fetchData = async () => {
        setLoader(true);
        try {
            const { data } = await axios.post(LIST_URL, { facility_id: props.selectedFacility }, getHeaders());
            setTableData(mapRows(extractRows(data)));
        } catch (error) {
            console.error("Error fetching medical records:", error);
        } finally {
            setLoader(false);
        }
    };

    const buildReportPayload = (isPrint) => (
        formData.isShowAllFacility
            ? {
                facility_id: '-999',
                is_print: isPrint
            }
            : { facility_id: localStorage.getItem("reportFacilityId"), is_print: isPrint }
    );

    const handleSubmit = async e => {
        e.preventDefault();
        if (!props.validateFacility()) return;
        showSpinner(true);
        try {
            const data = await axios.post(REPORT_URL, buildReportPayload(''), getHeaders());
            if (data.data.status == false) {
                toast.error(data.data.message);
            } else {
                toast.success("Medical Report Generated Successfully");
                const { blobUrl } = await createIframeReportBlobUrl(data.data);
                setIframeSrc(blobUrl);
            }
        } catch (error) {
            console.error("Error generating medical report:", error);
            toast.error("Failed to generate Medical Report");
        } finally {
            showSpinner(false);
        }
    };

    const handleSubmitCsv = async e => {
        e.preventDefault();
        if (!props.validateFacility()) return;
        showSpinner(true);
        try {
            const data = await axios.post(REPORT_URL, buildReportPayload('csv'), getHeaders());
            if (data.data.status == false) {
                toast.error(data.data.message);
            } else {
                const disposition = data.headers['content-disposition'];
                const filename = disposition
                    ? disposition.split('filename=')[1]?.replace(/"/g, '').trim()
                    : 'medical-report.csv';
                const url = URL.createObjectURL(new Blob([data.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', filename);
                document.body.appendChild(link);
                link.click();
                link.parentNode.removeChild(link);
                URL.revokeObjectURL(url);
                toast.success("Medical Report Exported Successfully");
            }
        } catch (error) {
            console.error("Error exporting medical report:", error);
            toast.error("Failed to export Medical Report");
        } finally {
            showSpinner(false);
        }
    };

    const table = useAdvanceTable({
        data: tableData,
        columns,
        selection: false,
        sortable: true,
        pagination: true
    });

    const table2 = useAdvanceTable({
        data: wholeData,
        columns,
        selection: false,
        sortable: true,
        pagination: true
    });

    const tableHeight = tableRef.current ? tableRef.current.clientHeight : 150;

    const renderTable = (rows) => (
        <Card className="mb-3 shadow-sm rounded-3">
            <Card.Body className="p-4" ref={tableRef}>
                {loader ? (
                    <div className="d-flex justify-content-center align-items-center" style={{ height: `${tableHeight}px` }}>
                        <Spinner animation="border" role="status" className="mx-auto"></Spinner>
                    </div>
                ) : rows.length > 0 ? (
                    <Row>
                        <Col xs="4" className="mb-2 view-patient-search-holder">
                            <AdvanceTableSearchBox placeholder="Search..." />
                        </Col>
                        <Col xs="12">
                            <AdvanceTable
                                headerClassName="bg-200 text-nowrap align-middle text-center fw-semibold"
                                rowClassName="text-nowrap align-middle text-center"
                                tableProps={{
                                    size: 'sm',
                                    striped: true,
                                    className: 'fs-12 mb-0 overflow-hidden table-bordered shadow-sm'
                                }}
                            />
                        </Col>
                    </Row>
                ) : (
                    <div className="d-flex justify-content-center align-items-center text-center" style={{ height: '150px' }}>
                        <p className="text-danger fw-bold mb-0">No data available for this option. Please select another Facility.</p>
                    </div>
                )}
            </Card.Body>
            <Card.Footer className="bg-light rounded-3 py-3">
                <div className="mt-3">
                    <AdvanceTableFooter rowInfo navButtons rowsPerPageSelection />
                </div>
            </Card.Footer>
        </Card>
    );

    const isActionDisabled = props.selectedFacility == '' && formData.isShowAllFacility == false;

    return (
        <div className="mt-4">
            <Form.Group className="mb-3">
                <Form.Check
                    type="checkbox"
                    label="All Facility"
                    name="isShowAllFacility"
                    onChange={(e) => {
                        props.setFlag(e.target.checked);
                        setFormData({ ...formData, isShowAllFacility: e.target.checked });
                    }}
                    checked={formData.isShowAllFacility}
                />
            </Form.Group>

            {formData.isShowAllFacility ? (
                <AdvanceTableProvider {...table2}>
                    {renderTable(wholeData)}
                </AdvanceTableProvider>
            ) : (
                <AdvanceTableProvider {...table}>
                    {renderTable(tableData)}
                </AdvanceTableProvider>
            )}

            <Form>
                <Row>
                    <Col xs={6} className="text-center">
                        {spinner ? (
                            <Button variant="primary" disabled className="mt-3 w-75 px-4 py-2">
                                <Spinner as="span" animation="grow" size="sm" role="status" aria-hidden="true" />
                                <span className="ms-2">Loading...</span>
                            </Button>
                        ) : (
                            <Button disabled={isActionDisabled} onClick={handleSubmit} color="primary" className="mt-3 w-75 px-4 py-2">
                                Download Report
                            </Button>
                        )}
                    </Col>
                    <Col xs={6} className="text-center">
                        {spinner ? (
                            <Button variant="primary" disabled className="mt-3 w-75 px-4 py-2">
                                <Spinner as="span" animation="grow" size="sm" role="status" aria-hidden="true" />
                                <span className="ms-2">Loading...</span>
                            </Button>
                        ) : (
                            <Button disabled={isActionDisabled} onClick={handleSubmitCsv} color="primary" className="mt-3 w-75 px-4 py-2">
                                Export csv
                            </Button>
                        )}
                    </Col>
                </Row>
                <Row className="mt-3">
                    <Col>
                        <div style={{ height: 0, overflow: 'hidden', width: 0 }}>
                            {iframeSrc && (
                                <iframe
                                    src={iframeSrc}
                                    style={{ width: "100%", height: "400px", border: "none", borderRadius: "8px" }}
                                    title="Preview Content"
                                />
                            )}
                        </div>
                    </Col>
                </Row>
                <Row className="mt-2">
                    <Col xs={12} className="text-end">
                        <Form.Label><span className="text-danger">*</span> indicates required field</Form.Label>
                    </Col>
                </Row>
            </Form>
        </div>
    );
};

MedicalReport.propTypes = {
    selectedFacility: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    setFlag: PropTypes.func,
    validateFacility: PropTypes.func
};

export default MedicalReport;
